import { z } from 'zod';

import { isApiError } from '~/domains/services/utils/isApiError';

type DeclineMessageKey =
  | 'deposits.errors.aeropay.declines.bankDecline'
  | 'deposits.errors.aeropay.declines.limitExceeded'
  | 'deposits.errors.aeropay.declines.insufficientFunds'
  | 'deposits.errors.aeropay.declines.accountRestricted'
  | 'deposits.errors.aeropay.declines.bankNotVerified';

const DECLINE_MESSAGE_KEYS = new Map<string, DeclineMessageKey>([
  ['BANK_DECLINE', 'deposits.errors.aeropay.declines.bankDecline'],
  ['LIMIT_EXCEEDED', 'deposits.errors.aeropay.declines.limitExceeded'],
  ['INSUFFICIENT_FUNDS', 'deposits.errors.aeropay.declines.insufficientFunds'],
  ['RISKY', 'deposits.errors.aeropay.declines.accountRestricted'],
  ['INVALID_VERIFICATION', 'deposits.errors.aeropay.declines.bankNotVerified'],
]);

const declineBodySchema = z.object({
  id: z.string().optional().catch(undefined),
  data: z.object({
    declineCategory: z.string(),
    declineSource: z.literal('AEROPAY'),
  }),
});

export type AeropayDecline = {
  declineCategory: string;
  declineSource: 'AEROPAY';
  requestId?: string;
};

export function readAeropayDecline(error: unknown): AeropayDecline | undefined {
  if (!isApiError(error)) {
    return undefined;
  }

  const parsed = declineBodySchema.safeParse(error.response?.data);

  if (!parsed.success) {
    return undefined;
  }

  const { id, data } = parsed.data;

  return {
    declineCategory: data.declineCategory,
    declineSource: data.declineSource,
    ...(id !== undefined ? { requestId: id } : {}),
  };
}

export function aeropayDeclineMessageKey({
  declineCategory,
}: AeropayDecline): DeclineMessageKey | undefined {
  return DECLINE_MESSAGE_KEYS.get(declineCategory);
}
